import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';

export const IS_NATIVE_AUTH = Capacitor.isNativePlatform();
const TOKEN_KEY = 'xtimer:auth-token';
const VERIFIER_KEY = 'xtimer:auth-verifier';
const base64url = (bytes) => btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
let tokenPromise;
const handledReturns = new Set();
export async function nativeAuthHeaders() {
  if (!IS_NATIVE_AUTH) return {};
  if (!tokenPromise) tokenPromise = Preferences.get({ key: TOKEN_KEY }).then(({ value }) => value);
  const token = await tokenPromise;
  return token ? { Authorization: `Bearer ${token}` } : {};
}
export async function clearNativeAuth() {
  if (!IS_NATIVE_AUTH) return;
  tokenPromise = Promise.resolve(null);
  await Preferences.remove({ key: TOKEN_KEY });
}
export async function startNativeLogin(apiBase) {
  const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  const challenge = base64url(new Uint8Array(digest));
  await Preferences.set({ key: VERIFIER_KEY, value: verifier });
  await Browser.open({ url: `${apiBase}/auth/google?nativeChallenge=${encodeURIComponent(challenge)}` });
}
export async function listenForNativeLogin(apiBase, onSuccess, onError) {
  if (!IS_NATIVE_AUTH) return () => {};
  let processing = false;
  const handle = async ({ url }) => {
    if (!url.startsWith('ch.yiagency.xtimer://auth') || processing || handledReturns.has(url)) return;
    const parsed = new URL(url);
    if (parsed.hostname !== 'auth') return;
    handledReturns.add(url);
    processing = true;
    try {
      const { value: verifier } = await Preferences.get({ key: VERIFIER_KEY });
      if (!verifier) return;
      if (parsed.searchParams.has('error')) throw new Error('login_failed');
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      let response;
      try { response = await fetch(`${apiBase}/auth/native/exchange`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: parsed.searchParams.get('code'), verifier }), signal: controller.signal
      }); } finally { clearTimeout(timeout); }
      if (!response.ok) throw new Error('login_failed');
      const { token } = await response.json();
      await Preferences.set({ key: TOKEN_KEY, value: token });
      tokenPromise = Promise.resolve(token);
      await Preferences.remove({ key: VERIFIER_KEY });
      try { await Browser.close(); } catch (_error) { /* Android closes its browser on return. */ }
      onSuccess();
    } catch (_error) { onError(); }
    finally { processing = false; }
  };
  const listener = await App.addListener('appUrlOpen', handle);
  const launch = await App.getLaunchUrl();
  if (launch?.url) await handle(launch);
  return () => listener.remove();
}
